import React from "react";
import { Stack, StackProps, Divider, Typography, Chip, Switch, styled } from "@mui/material";
import FormSection from "../../../Forms/FormSection/form-section";
import DataTable, { CellSize } from "../../../Table/table";
import ConfigFormIcon from "../../ConfigFormIcon";
import { CurrencyPriceInfos } from "../../../../Core/Api/Types/currency-price-infos";
import { DbOrderManualTaskTemplateFormFieldInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-form-field-infos";
import { MetadataFieldBaseTypeEnum } from "../../../../Core/Api/Types/metadata-field-base-type-enum";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DbOrderManualTaskTemplateInfos } from "../../../../Core/Api/Types/db-order-manual-task-template-infos";
import { useTranslation } from "react-i18next";
import { immerable } from "immer";

interface ConfigManualTaskFormRenderProps extends StackProps {
    tpl: DbOrderManualTaskTemplateInfos;
}

const ConfigManualTaskFormRender: React.FC<ConfigManualTaskFormRenderProps> = (props) => {
    const { t } = useTranslation();
    const { tpl, ...otherProps } = props;

    // Price totals sum
    const pricesTotals = React.useMemo((): CurrencyPriceInfos[] => {
        let prices: CurrencyPriceInfos[] = [];

        if (tpl) {
            tpl.jobs?.forEach((job, jIndex) => {
                job.tasks?.forEach((task, index) => {
                    if (task.prices) {
                        task.prices.forEach((taskPrice) => {
                            let priceMatch = prices.find((x) => x.currency === taskPrice.currency);


                            if (!priceMatch)
                                prices.push({
                                    currency: taskPrice.currency,
                                    price: taskPrice.price,
                                    priceDetails: [],
                                    [immerable]: false,
                                });
                            else priceMatch.price! += taskPrice.price!;

                        });
                    }
                });
            });
        }

        return prices;
    }, [tpl]);

    const getJobColumns = (headTitle: string, jobIndex: number): GridColDef[] => {
        return [
            {
                field: "name",
                headerName: headTitle,
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data) => (
                    <Stack sx={{ opacity: data?.row?.isDisabled ? 0.3 : 1 }}>
                        <Typography variant="body2">{data.value}</Typography>
                        <Typography variant="body2" color="text.tier">
                            {data?.row?.description}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: "isDisabled",
                headerName: t("common:option"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data) => (
                    <>
                        {!data?.row?.canBeDisabled ? (
                            <Chip label={t("common:mandatory")} color="info" size="small" />
                        ) : (
                            <Switch checked={!data.value} disabled />
                        )}
                    </>
                ),
            },
            {
                field: "prices",
                headerName: t("common:prices"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data) => (
                    <Stack direction="row" alignItems="center" sx={{ opacity: data?.row?.isDisabled ? 0.3 : 1 }}>
                        {data.value.map((price, index) => (
                            <Typography
                                variant="body1"
                                color="text.primary"
                                component="span"
                                ml={index !== data.value.length - 1 ? "" : 0.5}
                                key={index}
                            >
                                {price.price}
                                <Typography variant="body2" color="text.tier" component="span">
                                    &nbsp;{price.currency} {index !== data.value.length - 1 ? "/" : ""}
                                </Typography>
                            </Typography>
                        ))}
                        {(!data.value || data.value.length === 0) && (
                            <Typography variant="body2" color="text.tier" component="span">
                                &nbsp;{t("common:notDefined")}
                            </Typography>
                        )}
                    </Stack>
                ),
            },
        ];
    };

    const _renderField = (field: DbOrderManualTaskTemplateFormFieldInfos, fieldIndex: number) => {
        switch (field.baseType) {
            case MetadataFieldBaseTypeEnum.String:
                return field?.values?.map((v) => <Chip label={v} />) ?? "-";

            case MetadataFieldBaseTypeEnum.Long:
            case MetadataFieldBaseTypeEnum.Integer:
                break;
            case MetadataFieldBaseTypeEnum.Boolean:
                return (
                    <Typography color="text.tier">
                        {field.values?.includes("True") ? t("common:true") : t("common:false")}
                    </Typography>
                );
        }

        return <></>;
    };

    return (
        <>
            <StyledForm>
                <Stack spacing={1.5} width={"100%"} {...otherProps}>
                    <FormSection
                        icon={<ConfigFormIcon icon="import" />}
                        title={t("form:section.title.templateAndJobs")}
                        defaultExpanded
                    >
                        <Stack spacing={3}>
                            <Stack>
                                {tpl.jobs?.map((job, index) => (
                                    <DataTable
                                        noControls
                                        rows={job.tasks!}
                                        columns={getJobColumns(`Job #${index + 1} - ${job.name!}`, index)}
                                        rowHeight={44}
                                        columnHeaderHeight={32}
                                        hideFooter={true}
                                        key={`dt${index}`}
                                    />
                                ))}

                                <Divider sx={{ mb: 3, mt: 3 }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {t("common:total")}
                                        <Typography component="span" variant="body1" color="text.tier">
                                            &nbsp; (
                                            {tpl.isTreatedExternally
                                                ? t("component:configManualTaskFormRender.body.performedByThirdPart")
                                                : t("component:configManualTaskFormRender.body.performedByVdmTeams")}
                                            )
                                        </Typography>
                                    </Typography>
                                    <Stack direction="row" alignItems="center">
                                        {pricesTotals.map((price, index) => (
                                            <Typography variant="subtitle2" component="span" fontWeight={600} ml={0.5} key={`totalp${index}`}>
                                                {price.price}
                                                <Typography variant="subtitle2" component="span" fontWeight={600}>
                                                    {" "}
                                                    {price.currency} {index != pricesTotals.length - 1 ? "/" : ""}
                                                </Typography>
                                            </Typography>
                                        ))}
                                        {pricesTotals.length === 0 && (
                                            <Typography variant="subtitle2" component="span" fontWeight={600}>
                                                {t("common:notDefined")}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    </FormSection>

                    {tpl.formFields && tpl.formFields.length > 0 && (
                        <FormSection icon={<ConfigFormIcon icon="meta" />} title={t("form:section.title.metadataFormFields")}>
                            {tpl.formFields && tpl.formFields.length > 0 && (
                                <>
                                    {tpl.formFields.map((field, index) => (
                                        <Stack direction="row" justifyContent="space-between" mt={0.5} mb={0.5}>
                                            <Typography>{field.label}</Typography> {_renderField(field, index)}
                                        </Stack>
                                    ))}
                                </>
                            )}
                        </FormSection>
                    )}
                </Stack>
            </StyledForm>
        </>
    );
};

export default ConfigManualTaskFormRender;

const StyledForm = styled("form")(({ theme }) => ({}));
